import dateUtils from 'utils/Date';
import TIMEZONES from 'utils/TimeZones';

/**
 * Use DateTZ as you would use javascript Date object
 * You can add any other Date method not yet implemented here
 *
 * If no time zone is provided, or timezone doesn't exist,
 * it defaults to local user time zone
 */

function DateTZ(date, timeZone) {
    this._localUserTime = new Date(date);
    this._timeZoneTime = new Date(date);
    this._timeZone = timeZone;
    this._localOffset = 0;
    this._utcOffset = 0;
    this.updateOffset();
}

/**
 * This method analyzes if we are in Daylight Saving Time period of the year
 * for US and CANADA only and updates the offset and time accordingly.
 *
 * Since 2007 in Canada and US, daylight saving time begins on the second Sunday of March
 * and ends on the first Sunday of November, at 2:00am on local time.
 *
 * In the future, if this rule changes we must update this method to reflect the new rule.
 */

DateTZ.prototype.updateOffset = function () {
    const MARCH = 2;
    const NOVEMBER = 10;
    const SUNDAY = 0;

    //Assume it's standard time
    const timeZoneOffsets = (this._timeZone && TIMEZONES[this._timeZone]) || [this.getLocalTimeZoneOffset()];
    const utcLocalOffset = -this._localUserTime.getTimezoneOffset();
    let tzOffset = timeZoneOffsets[0] * 60 - utcLocalOffset;
    this._utcOffset = timeZoneOffsets[0] * 60;
    this._localOffset = tzOffset;
    this._timeZoneTime.setMinutes(this._timeZoneTime.getMinutes() + tzOffset);

    // If there's no DST in the given timezone, there's no need to adjust hours
    if (timeZoneOffsets.length < 2 || timeZoneOffsets[0] === timeZoneOffsets[1]) {
        return;
    }

    // Start of DST is 2nd Sunday of March
    const year = this._timeZoneTime.getFullYear();
    const secondSundayOfMarch = dateUtils.getNthDayOfMonth(2, SUNDAY, MARCH, year);
    const startDST = new Date(year, MARCH, secondSundayOfMarch, 2, 0, 0);

    // End of DST is first Sunday of November
    const firstSundayOfNovember = dateUtils.getNthDayOfMonth(1, SUNDAY, NOVEMBER, year);
    const endDST = new Date(year, NOVEMBER, firstSundayOfNovember, 2, 0, 0);

    // If DST is observed adjust the hour
    if (
        (dateUtils.isSameDay(this._timeZoneTime, startDST) && this._timeZoneTime.getHours() > 2) ||
        dateUtils.isDayBetween(this._timeZoneTime, startDST, endDST)
    ) {
        tzOffset = (timeZoneOffsets[1] - timeZoneOffsets[0]) * 60;
        this._timeZoneTime.setMinutes(this._timeZoneTime.getMinutes() + tzOffset);
        this._utcOffset = timeZoneOffsets[1] * 60;
        this._localOffset += tzOffset;
    }
};

DateTZ.prototype.getLocalTimeZoneOffset = function () {
    return -(this._localUserTime.getTimezoneOffset() / 60);
};

DateTZ.prototype.setDateTime = function (date) {
    this._timeZoneTime = dateUtils.clone(date);
};
DateTZ.prototype.getTime = function () {
    return this._timeZoneTime.getTime();
};
DateTZ.prototype.getFullYear = function () {
    return this._timeZoneTime.getFullYear();
};
DateTZ.prototype.getMonth = function () {
    return this._timeZoneTime.getMonth();
};
DateTZ.prototype.getDate = function () {
    return this._timeZoneTime.getDate();
};
DateTZ.prototype.getDay = function () {
    return this._timeZoneTime.getDay();
};
DateTZ.prototype.getHours = function () {
    return this._timeZoneTime.getHours();
};
DateTZ.prototype.getMinutes = function () {
    return this._timeZoneTime.getMinutes();
};
DateTZ.prototype.getSeconds = function () {
    return this._timeZoneTime.getSeconds();
};
DateTZ.prototype.getMilliseconds = function () {
    return this._timeZoneTime.getMilliseconds();
};
DateTZ.prototype.setTime = function () {
    return this._timeZoneTime.setTime();
};
DateTZ.prototype.setDate = function (...args) {
    return this._timeZoneTime.setDate(...args);
};
DateTZ.prototype.setMonth = function (...args) {
    return this._timeZoneTime.setMonth(...args);
};
DateTZ.prototype.setFullYear = function (...args) {
    return this._timeZoneTime.setFullYear(...args);
};
DateTZ.prototype.setHours = function (...args) {
    return this._timeZoneTime.setHours(...args);
};
DateTZ.prototype.setMinutes = function (...args) {
    return this._timeZoneTime.setMinutes(...args);
};
DateTZ.prototype.setSeconds = function (...args) {
    return this._timeZoneTime.setSeconds(...args);
};
DateTZ.prototype.toLocaleTimeString = function (...args) {
    return this._timeZoneTime.toLocaleTimeString(...args);
};
DateTZ.prototype.toLocaleString = function (...args) {
    return this._timeZoneTime.toLocaleString(...args);
};
DateTZ.prototype.toDateString = function () {
    return (
        `${dateUtils.getShortenedWeekdaysArray()[this.getDay()]} ` +
        `${dateUtils.getShortenedMonthArray()[this.getMonth()]} ` +
        `${('0' + this.getDate()).slice(-2)} ` +
        `${this.getFullYear()}`
    );
};
DateTZ.prototype.toISOString = function () {
    const isoDateTime = dateUtils.clone(this._timeZoneTime);
    isoDateTime.setMinutes(isoDateTime.getMinutes() - this._utcOffset);
    const isoString =
        `${isoDateTime.getFullYear()}-` +
        `${('0' + (isoDateTime.getMonth() + 1)).slice(-2)}-` +
        `${('0' + isoDateTime.getDate()).slice(-2)}T` +
        `${('0' + isoDateTime.getHours()).slice(-2)}:` +
        `${('0' + isoDateTime.getMinutes()).slice(-2)}:` +
        `${('0' + isoDateTime.getSeconds()).slice(-2)}.` +
        `${('00' + isoDateTime.getMilliseconds()).slice(-3)}Z`;

    return isoString;
};

DateTZ.prototype.toString = function () {
    const offset = `${('0' + Math.abs(this._utcOffset / 60)).slice(-2)}00`;
    const string =
        `${this.toDateString()} ` +
        `${('0' + this.getHours()).slice(-2)}:` +
        `${('0' + this.getMinutes()).slice(-2)}:` +
        `${('0' + this.getSeconds()).slice(-2)} ` +
        `GMT${this._utcOffset >= 0 ? '+' : '-'}${offset}`;

    return string;
};

export default DateTZ;
